import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

export type Theme = "light" | "dark" | "system";

export interface LayoutState {
  sidebarCollapsed: boolean;
  sidebarAutoCollapsed: boolean;
  mobileMenuOpen: boolean;
  theme: Theme;
  systemDark: boolean;
  language: "en" | "pt";
  toggleSidebar: () => void;
  setSidebarAutoCollapsed: (collapsed: boolean) => void;
  setSidebarOpen: (open: boolean) => void;
  toggleMobileMenu: () => void;
  setMobileMenuOpen: (open: boolean) => void;
  setTheme: (theme: Theme) => void;
  setLanguage: (lang: "en" | "pt") => void;
}

const LAYOUT_STORE_KEY = "koachy-layout-store";

type PersistedLayoutState = Pick<
  LayoutState,
  "sidebarCollapsed" | "theme" | "language"
>;

const prefersDark = (): boolean =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-color-scheme: dark)").matches;

export const resolveTheme = (
  theme: Theme,
  systemDark: boolean,
): "light" | "dark" =>
  theme === "system" ? (systemDark ? "dark" : "light") : theme;

const applyTheme = (theme: Theme, systemDark: boolean): void => {
  if (typeof document === "undefined") return;
  document.documentElement.classList.toggle(
    "dark",
    resolveTheme(theme, systemDark) === "dark",
  );
};

const getBrowserLanguage = (): "en" | "pt" => {
  if (typeof window === "undefined") return "en";

  const browserLanguages = navigator.languages || [navigator.language];

  for (const lang of browserLanguages) {
    const baseLanguage = lang.split("-")[0].toLowerCase();
    if (baseLanguage === "pt" || baseLanguage === "en") {
      return baseLanguage as "pt" | "en";
    }
  }

  return "en";
};

export const useLayoutStore = create<LayoutState>()(
  devtools(
    persist(
      (set) => ({
        sidebarCollapsed: false,
        sidebarAutoCollapsed: false,
        mobileMenuOpen: false,
        theme: "system",
        systemDark: prefersDark(),
        language: getBrowserLanguage(),

        toggleSidebar: () =>
          set(
            (state) => ({ sidebarCollapsed: !state.sidebarCollapsed }),
            false,
            "toggleSidebar",
          ),

        setSidebarAutoCollapsed: (collapsed) =>
          set(
            { sidebarAutoCollapsed: collapsed },
            false,
            "setSidebarAutoCollapsed",
          ),

        setSidebarOpen: (open) =>
          set(
            (state) => ({
              sidebarCollapsed: !open,
              sidebarAutoCollapsed: open ? false : state.sidebarAutoCollapsed,
            }),
            false,
            "setSidebarOpen",
          ),

        toggleMobileMenu: () =>
          set(
            (state) => ({ mobileMenuOpen: !state.mobileMenuOpen }),
            false,
            "toggleMobileMenu",
          ),

        setMobileMenuOpen: (open) =>
          set({ mobileMenuOpen: open }, false, "setMobileMenuOpen"),

        setTheme: (theme) =>
          set(
            (state) => {
              applyTheme(theme, state.systemDark);
              return { theme };
            },
            false,
            "setTheme",
          ),

        setLanguage: (lang) => set({ language: lang }, false, "setLanguage"),
      }),
      {
        name: LAYOUT_STORE_KEY,
        version: 1,
        migrate: (persisted, version) => {
          const { darkMode, ...state } = persisted as PersistedLayoutState & {
            darkMode?: boolean;
          };
          if (version === 0 && darkMode !== undefined) {
            const theme: Theme = darkMode ? "dark" : "light";
            return { ...state, theme };
          }
          return state;
        },
        partialize: (state) => ({
          sidebarCollapsed: state.sidebarCollapsed,
          theme: state.theme,
          language: state.language,
        }),
        onRehydrateStorage: () => (state) => {
          if (state?.theme) {
            applyTheme(state.theme, state.systemDark);
          }
        },
      },
    ),
    {
      name: "layout-store",
    },
  ),
);

// Collapsed if the user's persisted preference says so, or a wizard route has
// temporarily forced the icon rail via `setSidebarAutoCollapsed`.
export const useSidebarCollapsed = (): boolean =>
  useLayoutStore(
    (state) => state.sidebarCollapsed || state.sidebarAutoCollapsed,
  );

// Single OS listener for the app; components read `systemDark` from the store.
if (typeof window !== "undefined") {
  window
    .matchMedia("(prefers-color-scheme: dark)")
    .addEventListener("change", (event) => {
      useLayoutStore.setState(
        { systemDark: event.matches },
        false,
        "systemThemeChanged",
      );
      const { theme } = useLayoutStore.getState();
      if (theme === "system") {
        applyTheme(theme, event.matches);
      }
    });
}
